import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthProvider";
import {
  friendlyMessage,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "./projectApi";
import type { Notification } from "../models/project";

// The account's notification feed, in its own controller because two surfaces read it: 홈 (the unread
// roll-up on the landing screen) and 내 활동 (the full list). Mark-read applies to local state only
// after the server acknowledges, so the badge count and the row can never disagree.
//
// The count is DERIVED from the feed rather than fetched: /api/account/notifications returns the whole
// feed unpaginated, so the separate /unread-count call was a second round-trip for a number we already
// hold — and one that could drift from the list it labels.
export function useNotifications() {
  const { status, token } = useAuth();
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const getToken = useMemo(() => () => tokenRef.current(), []);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setNotifications(await listNotifications(getToken));
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (status === "authenticated") void reload();
  }, [status, reload]);

  const markRead = useCallback(async (id: string) => {
    setError(null);
    try {
      await markNotificationRead(getToken, id);
      setNotifications((cur) => cur.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }, [getToken]);

  const markAllRead = useCallback(async () => {
    setError(null);
    try {
      await markAllNotificationsRead(getToken);
      setNotifications((cur) => cur.map((n) => ({ ...n, read: true })));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }, [getToken]);

  const unread = useMemo(() => notifications.filter((n) => !n.read), [notifications]);

  return {
    notifications,
    /** Just the unread ones — what 홈 shows. */
    unread,
    unreadCount: unread.length,
    loading,
    error,
    clearError: () => setError(null),
    reload,
    markRead,
    markAllRead,
  };
}

export type NotificationsController = ReturnType<typeof useNotifications>;
